import PropTypes from 'prop-types';
import Immutable from 'seamless-immutable';
import { push } from 'connected-react-router';
import { reset } from 'redux-form';
import ReactGA from 'react-ga';
import { retrieveErrors, retrieveSuccessMessage } from '@widergy/web-utils/lib/message';
import { existsErrorCode } from '@widergy/web-utils/lib/errorHandling';
import { retrievePublicUserId } from '@widergy/web-utils/lib/analytics';
import { ERROR_CODES } from '@widergy/web-utils';
import Polling from '@widergy/polling-handling';
import { AlertHandler } from '@widergy/energy-ui';

import * as AuthService from 'services/AuthService';
import { ROUTES } from 'constants/routes';
import { appConfig } from 'config/customization';
import { message } from 'utils/stringUtils';
import { SIGNUP_IDENTITY_VALIDATION } from 'constants/reduxForms';
import { initialNavigation } from 'utils/routerUtils';
import { actionCreators as UtilityActions } from 'redux/utility/actions';
import { actionCreators as accountActionCreators } from 'redux/accountHandlers';

import { stringArrayToObject, retrieveSignUpNextSteps } from './utils';

/* ------------- Auth actions ------------- */
export const actions = stringArrayToObject(
  [
    'SET_CURRENT_USER',
    'SET_ENTERED_URL',
    'INIT',
    'INIT_SUCCESS',
    'INIT_FAILURE',
    'SIGN_IN',
    'SIGN_IN_SUCCESS',
    'SIGN_IN_FAILURE',
    'SIGN_OUT',
    'RESEND_VERIFICATION_CODE',
    'VERIFICATION_CODE_SENT',
    'GET_IDENTITY_VALIDATION_FORM',
    'GET_IDENTITY_VALIDATION_FORM_SUCCESS',
    'GET_IDENTITY_VALIDATION_FORM_FAILURE',
    'VALIDATE_IDENTITY_ANSWERS',
    'VALIDATE_IDENTITY_ANSWERS_SUCCESS',
    'VALIDATE_IDENTITY_ANSWERS_FAILURE',
    'VALIDATE_EMAIL',
    'VALIDATE_EMAIL_SUCCESS',
    'VALIDATE_EMAIL_FAILURE',
    'GET_USER_INFORMATION',
    'GET_USER_INFORMATION_SUCCESS',
    'GET_USER_INFORMATION_FAILURE',
    'GET_USER_PROFILE',
    'GET_USER_PROFILE_SUCCESS',
    'GET_USER_PROFILE_FAILURE',
    'UPDATE_USER_PROFILE',
    'UPDATE_USER_PROFILE_SUCCESS',
    'UPDATE_USER_PROFILE_FAILURE',
    'RECOVERY_PASSWORD_REQUEST',
    'RECOVERY_PASSWORD_REQUEST_SUCCESS',
    'RECOVERY_PASSWORD_REQUEST_FAILURE',
    'VALIDATE_TOKEN',
    'VALIDATE_TOKEN_SUCCESS',
    'VALIDATE_TOKEN_FAILURE',
    'RECOVERY_PASSWORD',
    'RECOVERY_PASSWORD_SUCCESS',
    'RECOVERY_PASSWORD_FAILURE',
    'SIGN_UP',
    'SIGN_UP_SUCCESS',
    'SIGN_UP_FAILURE',
    'CHANGE_PASSWORD',
    'CHANGE_PASSWORD_FAILURE',
    'CHANGE_PASSWORD_SUCCESS',
    'CHANGE_PRIMARY_EMAIL',
    'CHANGE_PRIMARY_EMAIL_FAILURE',
    'CHANGE_PRIMARY_EMAIL_SUCCESS',
    'UPDATE_CHANGE_PRIMARY_EMAIL_STEP',
    'GET_USER_IDENTIFICATION',
    'GET_USER_IDENTIFICATION_SUCCESS',
    'GET_USER_IDENTIFICATION_FAILURE',
    'SIGN_UP_UPDATE_USER_INFORMATION',
    'SIGN_UP_UPDATE_STEP',
    'SIGN_UP_RESTART',
    'SIGN_UP_RESET_INFO',
    'SIGN_UP_SET_INITIAL_ACCOUNT',
    'SIGN_UP_SET_RELATED_ACCOUNTS',
    'SIGN_UP_CLEAR_USER_IDENTIFICATION',
    'CLEAN_PUBLIC_USER_ID_ANALYTICS',
    'SET_PUBLIC_USER_ID_ANALYTICS',
    'CLEAN_USER_NEW_NOTIFICATIONS'
  ],
  '@@AUTH'
);

const privateActionCreators = {
  initSuccess: () => ({ type: actions.INIT_SUCCESS, target: 'session' }),
  initFailure: () => ({ type: actions.INIT_FAILURE, target: 'session' }),
  verificationCodeSent: () => ({ type: actions.VERIFICATION_CODE_SENT }),
  getUserProfileSuccess: userInfo => ({ type: actions.GET_USER_PROFILE_SUCCESS, payload: userInfo }),
  getUserProfileFailure: () => ({ type: actions.GET_USER_PROFILE_FAILURE }),
  getUserIdentificationSuccess: userIdentification => ({
    type: actions.GET_USER_IDENTIFICATION_SUCCESS,
    payload: userIdentification
  }),
  getUserIdentificationFailure: error => ({ type: actions.GET_USER_IDENTIFICATION_FAILURE, payload: error }),
  getIdentityValidationFormSuccess: userInfo => ({
    type: actions.GET_IDENTITY_VALIDATION_FORM_SUCCESS,
    payload: userInfo
  }),
  updateUserProfileSuccess: () => ({ type: actions.UPDATE_USER_PROFILE_SUCCESS }),
  updateUserProfileFailure: () => ({ type: actions.UPDATE_USER_PROFILE_FAILURE }),
  getUserInformationSuccess: userInfo => ({ type: actions.GET_USER_INFORMATION_SUCCESS, payload: userInfo }),
  getUserInformationFailure: () => ({ type: actions.GET_USER_INFORMATION_FAILURE }),
  changePasswordFailure: () => ({ type: actions.CHANGE_PASSWORD_FAILURE }),
  changePasswordSuccess: () => ({ type: actions.CHANGE_PASSWORD_SUCCESS }),
  changePrimaryEmailSuccess: () => ({ type: actions.CHANGE_PRIMARY_EMAIL_SUCCESS }),
  changePrimaryEmailFailure: () => ({ type: actions.CHANGE_PRIMARY_EMAIL_FAILURE }),
  validateEmailFailure: () => ({ type: actions.VALIDATE_EMAIL_FAILURE }),
  validateEmailSuccess: email => ({ type: actions.VALIDATE_EMAIL_SUCCESS, payload: email })
};

export const actionCreators = {
  setEnteredUrl: url => ({ type: actions.SET_ENTERED_URL, payload: url }),
  init: avoidNavigation => async (dispatch, getState) => {
    dispatch({ type: actions.INIT, target: 'session' });
    const storedSession = AuthService.authInit();
    await dispatch(UtilityActions.healthCheck());
    const { healthCheck } = getState().utility;
    if (storedSession && healthCheck && healthCheck.healthy) {
      await dispatch(actionCreators.getUserInformation());
      const { currentUser } = getState().auth;
      if (!currentUser) {
        dispatch(actionCreators.signOut());
        dispatch(privateActionCreators.initFailure());
        if (!avoidNavigation) await initialNavigation(dispatch, getState);
      } else {
        await dispatch(actionCreators.setCurrentUser(currentUser));
        await dispatch(UtilityActions.getAutomaticDebitForm());
        await dispatch(UtilityActions.getDigitalBillForm());
        dispatch({ type: actions.CLEAN_PUBLIC_USER_ID_ANALYTICS }); // To avoid duplicate user id in GA.
        await dispatch(
          accountActionCreators.getAccountsAsync(currentUser.id, appConfig.shouldUseAccOptimization)
        );
        dispatch(privateActionCreators.initSuccess());
        if (!avoidNavigation) await initialNavigation(dispatch, getState);
      }
    } else {
      dispatch(privateActionCreators.initFailure());
      if (!avoidNavigation) await initialNavigation(dispatch, getState);
    }
  },
  setPublicUserId: () => ({ type: actions.SET_PUBLIC_USER_ID_ANALYTICS }),
  setCurrentUser: user => async (dispatch, getState) => {
    dispatch({ type: actions.SET_CURRENT_USER, payload: user });
    ReactGA.set({ userId: getState().auth.currentUser && getState().auth.currentUser.id });
  },
  signIn(authData) {
    return async dispatch => {
      dispatch({ type: actions.SIGN_IN });
      const response = await AuthService.signIn(authData);
      if (response.ok) {
        dispatch(actionCreators.signInSuccess(response));
      } else {
        dispatch(actionCreators.signInFailure(response, authData));
      }
    };
  },
  signInSuccess: response => async dispatch => {
    AuthService.saveSession(response.data.access_token);
    dispatch(actionCreators.init());
    dispatch({ type: actions.SIGN_IN_SUCCESS });
  },
  signInFailure: (response, authData = {}) => async dispatch => {
    if (existsErrorCode(ERROR_CODES.NOT_CONFIRMED, response.data)) {
      const userInfo = {
        email: response.data.email,
        documentNumber: authData.document_number,
        documentType: authData.document_type,
        password: authData.password
      };
      dispatch(actionCreators.restartSignUp(5, userInfo));
      AlertHandler.info(message('Signup:notConfirmed'));
      dispatch({ type: actions.SIGN_IN_FAILURE, payload: ERROR_CODES.NOT_CONFIRMED });
    } else {
      const connectionIssue =
        response.problem === ERROR_CODES.NETWORK_ERROR || response.problem === ERROR_CODES.TIMEOUT_ERROR;
      if (existsErrorCode(ERROR_CODES.INVALID_CREDENTIALS, response.data)) {
        AlertHandler.error(retrieveErrors(response.data, message('DefaultMessages:signInFailure')));
        dispatch({ type: actions.SIGN_IN_FAILURE, payload: ERROR_CODES.INVALID_CREDENTIALS });
      } else if (connectionIssue) {
        AlertHandler.error(retrieveErrors(response.data, message('DefaultMessages:signInFailure')));
        dispatch({ type: actions.SIGN_IN_FAILURE, payload: ERROR_CODES.NETWORK_ERROR });
      } else {
        AlertHandler.error(retrieveErrors(response.data, message('DefaultMessages:signInFailure')));
        dispatch({ type: actions.SIGN_IN_FAILURE });
      }
    }
  },
  signUp() {
    return async (dispatch, getState) => {
      const { userInformation } = getState().auth.signUpFlow;
      dispatch({ type: actions.SIGN_UP });
      const response = await AuthService.signUp(userInformation);
      if (response.ok) {
        dispatch({ type: actions.SIGN_UP_SUCCESS });
        dispatch(actionCreators.updateSignUpStep());
      } else {
        dispatch({ type: actions.SIGN_UP_FAILURE });
        AlertHandler.error(retrieveErrors(response.data, message('DefaultMessages:signUpFailure')));
      }
    };
  },
  updateChangePrimaryEmailStep: stepNumber => ({
    type: actions.UPDATE_CHANGE_PRIMARY_EMAIL_STEP,
    payload: stepNumber
  }),
  changePrimaryEmail: email => async dispatch => {
    dispatch({ type: actions.CHANGE_PRIMARY_EMAIL });
    const response = await Polling.handle(AuthService.changePrimaryEmail(email));

    if (response.error) {
      dispatch(privateActionCreators.changePrimaryEmailFailure());
      AlertHandler.error(
        retrieveErrors(response.error, message('DefaultMessages:changePrimaryEmailFailure'))
      );
    } else {
      dispatch(privateActionCreators.changePrimaryEmailSuccess());
      AlertHandler.success(
        retrieveSuccessMessage(response.data, message('DefaultMessages:changePrimaryEmailSuccess'))
      );
      dispatch(actionCreators.updateChangePrimaryEmailStep(2));
    }
  },
  validateChangeEmail: (email, code) => async dispatch => {
    dispatch({ type: actions.VALIDATE_EMAIL });
    const response = await Polling.handle(AuthService.validateChangeEmail(email, code));

    if (response.error) {
      dispatch(privateActionCreators.validateEmailFailure());
      AlertHandler.error(
        retrieveErrors(response.error, message('DefaultMessages:validateChangeEmailFailure'))
      );
    } else {
      dispatch(privateActionCreators.validateEmailSuccess(email));
      AlertHandler.success(
        retrieveSuccessMessage(response.data, message('DefaultMessages:validateChangeEmailSuccess'))
      );
      // To avoid showing step one when modal transition is closing
      setTimeout(() => dispatch(actionCreators.updateChangePrimaryEmailStep(1)), 200);
      dispatch(push(ROUTES.ACCOUNT_STATUS));
    }
  },
  validateEmail: confirmationCode => async (dispatch, getState) => {
    dispatch({ type: actions.VALIDATE_EMAIL });
    const { userInformation } = getState().auth.signUpFlow;
    const response = await Polling.handle(
      AuthService.verifyEmail(
        confirmationCode,
        userInformation.email,
        userInformation.password,
        userInformation.documentNumber,
        userInformation.documentType
      )
    );
    if (response.error) {
      dispatch({ type: actions.VALIDATE_EMAIL_FAILURE });
      AlertHandler.error(retrieveErrors(response.error, message('DefaultMessages:validateEmailFailure')));
    } else {
      AuthService.saveSession(response.data.access_token);
      await dispatch(actionCreators.init());
      dispatch({ type: actions.VALIDATE_EMAIL_SUCCESS });
    }
  },
  resendVerificationCode(email, docType, docNumber) {
    return async dispatch => {
      dispatch({ type: actions.RESEND_VERIFICATION_CODE });
      const response = await AuthService.resendVerificationCode(email, docType, docNumber);
      if (response.ok) {
        dispatch(privateActionCreators.verificationCodeSent());
        AlertHandler.success(
          retrieveSuccessMessage(response.data, message('DefaultMessages:resendVerificationCodeSuccess'))
        );
      } else {
        dispatch(privateActionCreators.verificationCodeSent());
        AlertHandler.error(
          retrieveErrors(response.data, message('DefaultMessages:resendVerificationCodeFailure'))
        );
      }
    };
  },
  newPasswordRequest(email) {
    return async dispatch => {
      dispatch({ type: actions.RECOVERY_PASSWORD_REQUEST });
      const response = await AuthService.newPasswordRequest(email);
      if (response.ok) {
        dispatch({ type: actions.RECOVERY_PASSWORD_REQUEST_SUCCESS });
        dispatch(push(ROUTES.SIGN_IN));
        AlertHandler.success(
          retrieveSuccessMessage(response.data, message('DefaultMessages:newPasswordRequestSuccess'))
        );
      } else {
        dispatch({ type: actions.RECOVERY_PASSWORD_REQUEST_FAILURE });
        AlertHandler.error(
          retrieveErrors(response.data, message('DefaultMessages:newPasswordRequestFailure'))
        );
      }
    };
  },
  changePassword(currentPwd, pwd, pwdConfirm) {
    return async dispatch => {
      if (pwd !== currentPwd) {
        if (pwd === pwdConfirm) {
          const response = await AuthService.changePassword(currentPwd, pwd, pwdConfirm);
          if (response.ok) {
            AlertHandler.success(
              retrieveSuccessMessage(response.data, message('DefaultMessages:changePasswordSuccess'))
            );
            dispatch(privateActionCreators.changePasswordSuccess());
          } else {
            AlertHandler.error(
              retrieveErrors(response.data, message('DefaultMessages:changePasswordFailure'))
            );
            dispatch(privateActionCreators.changePasswordFailure());
          }
        } else {
          AlertHandler.error(message('DefaultMessages:authInvalidNewPasswords'));
          dispatch(privateActionCreators.changePasswordFailure());
        }
      } else {
        AlertHandler.error(message('DefaultMessages:samePasswords'));
        dispatch(privateActionCreators.changePasswordFailure());
      }
    };
  },
  validateToken(resetPasswordToken) {
    return async dispatch => {
      dispatch({ type: actions.VALIDATE_TOKEN });
      const response = await AuthService.validateToken(resetPasswordToken);
      if (!response.ok) {
        dispatch({ type: actions.VALIDATE_TOKEN_FAILURE });
        dispatch(push(ROUTES.SIGN_IN));
        AlertHandler.error(retrieveErrors(response.data, message('DefaultMessages:tokenError')));
      } else {
        dispatch({ type: actions.VALIDATE_TOKEN_SUCCESS });
      }
    };
  },

  recoveryPassword(userInfo, resetPasswordToken, externalToken) {
    return async dispatch => {
      dispatch({ type: actions.RECOVERY_PASSWORD });
      if (userInfo.password === userInfo.password_confirmation) {
        const response = await AuthService.recoveryPassword(userInfo, resetPasswordToken, externalToken);
        if (response.ok) {
          dispatch({ type: actions.RECOVERY_PASSWORD_SUCCESS });

          AlertHandler.success(
            retrieveSuccessMessage(response.data, message('DefaultMessages:recoveryPasswordSuccess'))
          );
          dispatch(push(ROUTES.SIGN_IN));
        } else {
          dispatch({ type: actions.RECOVERY_PASSWORD_FAILURE });

          AlertHandler.error(
            retrieveErrors(response.data, message('DefaultMessages:recoveryPasswordFailure'))
          );
        }
      } else {
        dispatch({ type: actions.RECOVERY_PASSWORD_FAILURE });
        AlertHandler.error(message('DefaultMessages:authInvalidNewPasswords'));
      }
    };
  },
  signOut: origin => async dispatch => {
    AuthService.signOut();
    dispatch(actionCreators.setPublicUserId());
    dispatch({ type: actions.SIGN_OUT, payload: origin });
  },
  getIdentityValidationForm: userInfo => async dispatch => {
    dispatch({ type: actions.GET_IDENTITY_VALIDATION_FORM });
    const response = await Polling.handle(AuthService.getIdentityValidationForm(userInfo));
    if (response.error) {
      AlertHandler.error(
        retrieveErrors(response.error, message('DefaultMessages:getIdentityValidationFormFailure'))
      );
      dispatch(actionCreators.restartSignUp(1));
    } else {
      dispatch(privateActionCreators.getIdentityValidationFormSuccess(response.data));
    }
  },
  validateIdentityAnswers(answers, userInformation) {
    return async (dispatch, getState) => {
      dispatch({ type: actions.VALIDATE_IDENTITY_ANSWERS });
      const { identityValidationForm } = getState().auth;
      const questionResponses = {
        provider: identityValidationForm.provider,
        questions_id: identityValidationForm.id,
        answers: identityValidationForm.questions.map(question => {
          const options = { ...question.answers };
          const responseKey = Object.keys(options).filter(
            key => options[key] === answers[`id${question.id}`]
          )[0];
          return {
            question_id: question.id,
            response_key: responseKey || null,
            response_value: answers[`id${question.id}`]
          };
        })
      };
      const response = await Polling.handle(AuthService.validateIdentityAnswers(questionResponses));
      if (response.error) {
        dispatch({ type: actions.VALIDATE_IDENTITY_ANSWERS_FAILURE });
        AlertHandler.error(
          retrieveErrors(response.error, message('DefaultMessages:validateIdentityAnswersFailure'))
        );
        dispatch(reset(SIGNUP_IDENTITY_VALIDATION));
        dispatch(actionCreators.getIdentityValidationForm(userInformation));
      } else {
        dispatch({ type: actions.VALIDATE_IDENTITY_ANSWERS_SUCCESS });
        dispatch(actionCreators.updateSignUpStep());
      }
    };
  },
  getUserInformation() {
    return async dispatch => {
      dispatch({ type: actions.GET_USER_INFORMATION });
      const response = await Polling.handle(AuthService.getUserInformation());
      if (response.error) {
        dispatch(privateActionCreators.getUserInformationFailure());
        AlertHandler.error(
          retrieveErrors(response.error, message('DefaultMessages:getUserInformationFailure'))
        );
      } else {
        dispatch(privateActionCreators.getUserInformationSuccess(response.data));
      }
    };
  },
  getUserProfile(handleError = true) {
    return async dispatch => {
      dispatch({ type: actions.GET_USER_PROFILE });
      const response = await Polling.handle(AuthService.getUserProfile());

      if (response.error) {
        dispatch(privateActionCreators.getUserProfileFailure());
        if (handleError) {
          dispatch(push(ROUTES.ACCOUNT_STATUS));
          AlertHandler.error(
            retrieveErrors(response.error, message('DefaultMessages:getUserProfileFailure'))
          );
        }
      } else {
        dispatch(privateActionCreators.getUserProfileSuccess(response.data));
      }
    };
  },
  updateUserProfile(userInfo, password) {
    return async dispatch => {
      dispatch({ type: actions.UPDATE_USER_PROFILE });
      const response = await Polling.handle(AuthService.updateUserProfile(userInfo, password));
      if (response.error) {
        const error = retrieveErrors(response.error, message('DefaultMessages:updateUserProfileFailure'));
        AlertHandler.error(error);
        dispatch(privateActionCreators.updateUserProfileFailure(error));
      } else {
        await dispatch(privateActionCreators.updateUserProfileSuccess());
        dispatch(push(ROUTES.ACCOUNT_STATUS));
        AlertHandler.success(
          retrieveSuccessMessage(response.data, message('DefaultMessages:updateUserProfileSuccess'))
        );
      }
    };
  },
  getUserIdentification: userData => async dispatch => {
    dispatch({ type: actions.GET_USER_IDENTIFICATION });
    const response = await Polling.handle(AuthService.getUserIdentification(userData));
    if (response.error) {
      dispatch(privateActionCreators.getUserIdentificationFailure(response.error));
      AlertHandler.error(
        retrieveErrors(response.error, message('DefaultMessages:getUserIdentificationFailure'))
      );
    } else {
      dispatch(privateActionCreators.getUserIdentificationSuccess(response.data));
    }
  },
  updateSignUpUserInformation: userInformation => async dispatch => {
    dispatch({
      type: actions.SIGN_UP_UPDATE_USER_INFORMATION,
      payload: userInformation
    });
  },
  updateSignUpStep: () => async (dispatch, getState) => {
    const { nextStepOnSuccess } = getState().auth.signUpFlow;
    const nextStep = retrieveSignUpNextSteps(nextStepOnSuccess);
    dispatch({
      type: actions.SIGN_UP_UPDATE_STEP,
      payload: { currentStep: nextStepOnSuccess, nextStepOnSuccess: nextStep }
    });
  },
  restartSignUp: (step, userInformation = {}) => async dispatch => {
    await dispatch({ type: actions.SIGN_UP_RESTART, payload: { nextStepOnSuccess: step, userInformation } });
    await dispatch(actionCreators.updateSignUpStep());
    dispatch(push(ROUTES.SIGN_UP));
  },
  resetSignUpInfo: () => ({ type: actions.SIGN_UP_RESET_INFO }),
  signUpSetInitialAccount: clientNumber => async dispatch => {
    dispatch({
      type: actions.SIGN_UP_SET_INITIAL_ACCOUNT,
      payload: clientNumber
    });
  },
  signUpSetRelatedAccounts: (clientNumbers = []) => async dispatch => {
    dispatch({
      type: actions.SIGN_UP_SET_RELATED_ACCOUNTS,
      payload: clientNumbers
    });
    if (clientNumbers.length > 0) {
      dispatch(accountActionCreators.addAccounts(clientNumbers));
    } else {
      dispatch(push(ROUTES.ACCOUNT_STATUS));
    }
  },
  cleanUserNewNotifications: () => ({ type: actions.CLEAN_USER_NEW_NOTIFICATIONS, payload: 0 }),
  signUpClearUserIdentification: () => ({ type: actions.SIGN_UP_CLEAR_USER_IDENTIFICATION })
};

/* ------------- Auth reducer ------------- */
const defaultState = {
  currentUser: null,
  sessionLoading: true,
  publicUserId: retrievePublicUserId(), // Analytics user id unless user sign in.
  identityValidationFormLoading: false,
  identityValidationForm: [],
  signInLoading: false,
  validateEmailLoading: false,
  signUpLoading: false,
  userIdentification: null,
  userIdentificationError: null,
  signUpFlow: {
    currentStep: 1,
    nextStepOnSuccess: appConfig.signUpFlow.IdentityValidation ? 2 : 3,
    userInformation: {},
    signUpInProgress: false,
    initialAccount: null,
    relatedAccounts: []
  },
  changePrimaryEmailLoading: false,
  changePrimaryEmailStep: 1
};

/* eslint-disable complexity */
export function reducer(state = Immutable(defaultState), action) {
  switch (action.type) {
    case actions.INIT: {
      return Immutable.merge(state, { sessionLoading: true });
    }
    case actions.INIT_SUCCESS: {
      return Immutable.merge(state, { sessionLoading: false });
    }
    case actions.INIT_FAILURE: {
      return Immutable.merge(state, { sessionLoading: false });
    }
    case actions.SET_CURRENT_USER: {
      return Immutable.merge(state, { currentUser: action.payload });
    }
    case actions.VERIFICATION_CODE_SENT:
    case actions.RESEND_VERIFICATION_CODE: {
      return Immutable.merge(state, {
        loadingVerificationCode: action.type === actions.RESEND_VERIFICATION_CODE
      });
    }
    case actions.SIGN_IN: {
      return Immutable.merge(state, { signInLoading: true });
    }
    case actions.SIGN_IN_SUCCESS:
    case actions.SIGN_IN_FAILURE: {
      return Immutable.merge(state, { signInLoading: false });
    }
    case actions.SIGN_OUT: {
      return Immutable.merge(state, {
        ...defaultState,
        sessionLoading: false
      });
    }
    case actions.GET_IDENTITY_VALIDATION_FORM: {
      return Immutable.merge(state, {
        identityValidationFormLoading: true
      });
    }
    case actions.GET_IDENTITY_VALIDATION_FORM_SUCCESS: {
      return Immutable.merge(state, {
        identityValidationForm: action.payload.validation_questions,
        identityValidationFormLoading: false,
        signUpFlow: {
          ...state.signUpFlow,
          userInformation: {
            ...state.signUpFlow.userInformation,
            first_name: action.payload.person.first_name || state.signUpFlow.userInformation.first_name,
            last_name: action.payload.person.last_name || state.signUpFlow.userInformation.last_name
          }
        }
      });
    }
    case actions.VALIDATE_IDENTITY_ANSWERS: {
      return Immutable.merge(state, { identityAnswersLoading: true });
    }
    case actions.VALIDATE_IDENTITY_ANSWERS_SUCCESS:
    case actions.VALIDATE_IDENTITY_ANSWERS_FAILURE: {
      return Immutable.merge(state, { identityAnswersLoading: false });
    }
    case actions.VALIDATE_EMAIL: {
      return Immutable.merge(state, { validateEmailLoading: true });
    }
    case actions.VALIDATE_EMAIL_SUCCESS: {
      return Immutable.merge(state, {
        validateEmailLoading: false,
        currentUser: { ...state.currentUser, email: action.payload }
      });
    }
    case actions.VALIDATE_EMAIL_FAILURE: {
      return Immutable.merge(state, { validateEmailLoading: false });
    }
    case actions.GET_USER_INFORMATION: {
      return Immutable.merge(state, { userInformationLoading: true });
    }
    case actions.GET_USER_INFORMATION_SUCCESS: {
      return Immutable.merge(state, {
        currentUser: action.payload,
        userInformationLoading: false
      });
    }
    case actions.GET_USER_INFORMATION_FAILURE: {
      return Immutable.merge(state, { userInformationLoading: false });
    }
    case actions.GET_USER_PROFILE: {
      return Immutable.merge(state, { userProfileLoading: true });
    }
    case actions.GET_USER_PROFILE_SUCCESS: {
      return Immutable.merge(state, {
        currentUser: action.payload,
        userProfileLoading: false
      });
    }
    case actions.GET_USER_PROFILE_FAILURE: {
      return Immutable.merge(state, { userProfileLoading: false });
    }
    case actions.UPDATE_USER_PROFILE: {
      return Immutable.merge(state, { updateUserProfileLoading: true });
    }
    case actions.UPDATE_USER_PROFILE_SUCCESS: {
      return Immutable.merge(state, {
        updateUserProfileLoading: false,
        currentUser: { ...state.currentUser, updated_profile: true }
      });
    }
    case actions.UPDATE_USER_PROFILE_FAILURE: {
      return Immutable.merge(state, { updateUserProfileLoading: false });
    }
    case actions.VALIDATE_TOKEN: {
      return Immutable.merge(state, { validateTokenLoading: true });
    }
    case actions.VALIDATE_TOKEN_SUCCESS: {
      return Immutable.merge(state, { validateTokenLoading: false });
    }
    case actions.VALIDATE_TOKEN_FAILURE: {
      return Immutable.merge(state, { validateTokenLoading: false });
    }
    case actions.RECOVERY_PASSWORD_REQUEST: {
      return Immutable.merge(state, { recoveryPasswordRequestLoading: true });
    }
    case actions.RECOVERY_PASSWORD_REQUEST_SUCCESS: {
      return Immutable.merge(state, { recoveryPasswordRequestLoading: false });
    }
    case actions.RECOVERY_PASSWORD_REQUEST_FAILURE: {
      return Immutable.merge(state, { recoveryPasswordRequestLoading: false });
    }
    case actions.RECOVERY_PASSWORD: {
      return Immutable.merge(state, { recoveryPasswordLoading: true });
    }
    case actions.RECOVERY_PASSWORD_SUCCESS: {
      return Immutable.merge(state, { recoveryPasswordLoading: false });
    }
    case actions.RECOVERY_PASSWORD_FAILURE: {
      return Immutable.merge(state, { recoveryPasswordLoading: false });
    }
    case actions.SIGN_UP: {
      return Immutable.merge(state, { signUpLoading: true });
    }
    case actions.SIGN_UP_SUCCESS: {
      return Immutable.merge(state, { signUpLoading: false });
    }
    case actions.SIGN_UP_FAILURE: {
      return Immutable.merge(state, { signUpLoading: false });
    }
    case actions.CLEAN_PUBLIC_USER_ID_ANALYTICS: {
      return Immutable.merge(state, {
        publicUserId: undefined
      });
    }
    case actions.SET_PUBLIC_USER_ID_ANALYTICS: {
      return Immutable.merge(state, {
        publicUserId: defaultState.publicUserId
      });
    }
    case actions.GET_USER_IDENTIFICATION: {
      return Immutable.merge(state, {
        userIdentification: null,
        userIdentificationLoading: true,
        userIdentificationError: null
      });
    }
    case actions.GET_USER_IDENTIFICATION_SUCCESS: {
      return Immutable.merge(state, { userIdentification: action.payload, userIdentificationLoading: false });
    }
    case actions.GET_USER_IDENTIFICATION_FAILURE: {
      return Immutable.merge(state, {
        userIdentificationError: action.payload,
        userIdentificationLoading: false
      });
    }
    case actions.SIGN_UP_UPDATE_USER_INFORMATION: {
      return Immutable.merge(state, {
        signUpFlow: {
          ...state.signUpFlow,
          userInformation: { ...state.signUpFlow.userInformation, ...action.payload }
        }
      });
    }
    case actions.SIGN_UP_UPDATE_STEP: {
      return Immutable.merge(state, {
        signUpFlow: {
          ...state.signUpFlow,
          currentStep: action.payload.currentStep,
          nextStepOnSuccess: action.payload.nextStepOnSuccess,
          signUpInProgress: true
        }
      });
    }
    case actions.SIGN_UP_RESTART: {
      return Immutable.merge(state, {
        signUpFlow: {
          ...state.signUpFlow,
          nextStepOnSuccess: action.payload.nextStepOnSuccess,
          userInformation: { ...state.signUpFlow.userInformation, ...action.payload.userInformation },
          signUpInProgress: true
        }
      });
    }
    case actions.SIGN_UP_RESET_INFO: {
      return Immutable.merge(state, {
        signUpFlow: defaultState.signUpFlow
      });
    }
    case actions.SIGN_UP_SET_INITIAL_ACCOUNT: {
      return Immutable.merge(state, {
        signUpFlow: { ...state.signUpFlow, initialAccount: action.payload }
      });
    }
    case actions.SIGN_UP_SET_RELATED_ACCOUNTS: {
      return Immutable.merge(state, {
        signUpFlow: {
          ...state.signUpFlow,
          relatedAccounts: [...state.signUpFlow.relatedAccounts, ...action.payload]
        }
      });
    }
    case actions.SIGN_UP_CLEAR_USER_IDENTIFICATION: {
      return Immutable.merge(state, {
        userIdentification: defaultState.userIdentification
      });
    }
    case actions.CHANGE_PRIMARY_EMAIL: {
      return Immutable.merge(state, {
        changePrimaryEmailLoading: true
      });
    }
    case actions.CHANGE_PRIMARY_EMAIL_SUCCESS:
    case actions.CHANGE_PRIMARY_EMAIL_FAILURE: {
      return Immutable.merge(state, {
        changePrimaryEmailLoading: false
      });
    }
    case actions.UPDATE_CHANGE_PRIMARY_EMAIL_STEP: {
      return Immutable.merge(state, {
        changePrimaryEmailStep: action.payload
      });
    }
    case actions.SET_ENTERED_URL: {
      return Immutable.merge(state, { enteredUrl: action.payload });
    }
    case actions.CLEAN_USER_NEW_NOTIFICATIONS: {
      return Immutable.merge(state, {
        currentUser: { ...state.currentUser, new_notifications: action.payload }
      });
    }
    default: {
      return state;
    }
  }
}
/* eslint-enable complexity */

/* ------------- Auth propTypes ------------- */

export const currentUserPropTypes = PropTypes.shape({
  email: PropTypes.string.isRequired,
  id: PropTypes.number,
  push_notifications_enabled: PropTypes.bool,
  total_points: PropTypes.number.isRequired
});
